import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Stack, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import MessageDisplay from '../../Shared/MessageDisplay';
import { setShowMessage, setIsLoading } from '../../../store/shared.slice';
import { formatDate } from '../../../utils/dateHelpers';
import { useState } from 'react';
import axios from 'axios';
import { apiString } from '../../../utils/constants';

interface AddToSchedulingListModalProps {
    open: boolean;
    onClose: () => void;
    providerId: string;
    onMemberAdded: () => Promise<void>;
}

interface SearchValues {
    LastName: string;
    FirstName: string;
    DateOfBirth: Date | null;
    AHCCCSID: string;
}

interface AppointmentSearchResult {
    Id: string;
    MemberId: string;
    MemberName: string;
    AHCCCSID: string;
    LastName: string;
    FirstName: string;
    DateOfBirth: string;
    Eligibility: string;
    MemberStatus: number;
    HealthPlan: string;
    PopulationName: string;
    AppointmentDateTime: Date;
    Address: string;
    ProviderNames: string[];
}

const AddToSchedulingListModal: React.FC<AddToSchedulingListModalProps> = ({ open, onClose, providerId, onMemberAdded }) => {
    const dispatch = useAppDispatch();
    const [searchValues, setSearchValues] = useState<SearchValues>({
        LastName: '',
        FirstName: '',
        DateOfBirth: null,
        AHCCCSID: '',
    });
    const [searchResults, setSearchResults] = useState<AppointmentSearchResult[] | null>(null);
    const [isValid, setIsValid] = useState(true);

    const checkIfFormIsValid = async () => {
        if (
            (searchValues.LastName.length > 0 && searchValues.DateOfBirth) ||
            (searchValues.FirstName.length > 0 && searchValues.DateOfBirth) ||
            searchValues.AHCCCSID.length > 0
        ) {
            setIsValid(true);
            await searchForAppointments();
        } else {
            setIsValid(false);
        }
    };

    const searchForAppointments = async () => {
        const DOB = searchValues.DateOfBirth ? formatDate(searchValues.DateOfBirth, 'MM/dd/yyyy') : null;
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get<AppointmentSearchResult[]>(
                `${apiString}/Scheduling/SearchUnconfirmedAppointments`,
                {
                    params: {
                        lastName: searchValues.LastName?.trim()?.toUpperCase(),
                        firstName: searchValues.FirstName?.trim()?.toUpperCase(),
                        dateOfBirth: DOB,
                        ahcccsId: searchValues.AHCCCSID,
                        providerId: providerId
                    }
                }
            );
            setSearchResults(response.data);
            setIsValid(true);
            dispatch(setIsLoading(false));
        } catch (error) {
            console.error('Error searching for appointments:', error);
            setIsValid(false);
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Error searching for appointments', 'error'));
        }
    };

    const addToList = async (appointment: AppointmentSearchResult) => {
        try {
            dispatch(setIsLoading(true));
            await axios.post(`${apiString}/Scheduling/AddAppointmentToConfirmationsList`, {
                appointmentId: appointment.Id,
                providerId: providerId
            });
            dispatch(setShowMessage(true, 'Appointment added to call list', 'success'));
            await onMemberAdded();
            handleClose();
        } catch (error) {
            console.error('Error adding to list:', error);
            dispatch(setShowMessage(true, 'Error adding appointment to list', 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const handleClose = () => {
        setSearchValues({
            LastName: '',
            FirstName: '',
            DateOfBirth: null,
            AHCCCSID: '',
        });
        setSearchResults(null);
        setIsValid(true);
        onClose();
    };

    const columns: GridColDef[] = [
        {
            field: 'AHCCCSID',
            headerName: 'AHCCCS ID',
            minWidth: 160,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 160,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
        },
        {
            field: 'DateOfBirth',
            headerName: 'Date of Birth',
            minWidth: 160,
            valueGetter: (value) => formatDate(new Date(value), 'MM/dd/yyyy'),
        },
        {
            field: 'AppointmentDateTime',
            headerName: 'Appointment',
            minWidth: 180,
            valueGetter: (value) => formatDate(new Date(value), 'MM/dd/yyyy h:mm aa'),
        },
        {
            field: 'MemberStatus',
            headerName: 'Status',
            minWidth: 160,
            valueGetter: (value) => `${MemberStatusEnum[value] || ''}`,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            minWidth: 200,
        },
        {
            field: 'PopulationName',
            headerName: 'Population Name',
            minWidth: 200,
        },
    ];

    const modalSize = searchResults ? 'lg' : 'sm';

    return (
        <>
            <MessageDisplay />
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth={modalSize}>
                <DialogTitle>Add to Confirmations Call List</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        <Stack spacing={2} paddingY={2}>
                            <TextField
                                autoComplete="off"
                                label="Last name"
                                variant="outlined"
                                value={searchValues.LastName}
                                onChange={(e) => setSearchValues({ ...searchValues, LastName: e.target.value })}
                            />
                            <TextField
                                autoComplete="off"
                                label="First name"
                                variant="outlined"
                                value={searchValues.FirstName}
                                onChange={(e) => setSearchValues({ ...searchValues, FirstName: e.target.value })}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    label="Date of Birth"
                                    onChange={(newValue: Date) => setSearchValues({ ...searchValues, DateOfBirth: newValue })}
                                    value={searchValues.DateOfBirth}
                                    slotProps={{
                                        textField: {
                                            autoComplete: 'off',
                                            required: true,
                                            error: !isValid && searchValues.DateOfBirth === null,
                                            helperText:
                                                !isValid && searchValues.DateOfBirth === null
                                                    ? 'Search by Date of Birth and First/Last Name or AHCCCS ID'
                                                    : '',
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                or
                            </Typography>
                            <TextField
                                autoComplete="off"
                                label="AHCCCS ID"
                                variant="outlined"
                                required
                                value={searchValues.AHCCCSID}
                                error={!isValid && searchValues.AHCCCSID === ''}
                                helperText={
                                    !isValid && searchValues.AHCCCSID === ''
                                        ? 'Search by Date of Birth and First/Last Name or AHCCCS ID'
                                        : ''
                                }
                                onChange={(e) => setSearchValues({ ...searchValues, AHCCCSID: e.target.value })}
                            />
                        </Stack>
                    </FormGroup>
                    {searchResults && (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                Search results
                            </Typography>
                            <div style={{ height: '30vh', width: '100%' }}>
                                <DataGrid
                                    rows={searchResults || []}
                                    columns={columns}
                                    paginationModel={{ pageSize: 3, page: 0 }}
                                    pageSizeOptions={[]}
                                    getRowId={(row) => row.Id}
                                    getRowHeight={() => 'auto'}
                                    onRowClick={(params) => {
                                        addToList(params.row);
                                    }}
                                    sx={{
                                        [`& .${gridClasses.cell}`]: {
                                            py: 1,
                                        },
                                    }}
                                    localeText={{
                                        noRowsLabel:
                                            'No unconfirmed appointments found for this member with the selected provider.',
                                    }}
                                />
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={{ minWidth: '120px' }} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" sx={{ minWidth: '120px' }} color="primary" onClick={checkIfFormIsValid}>
                        Search
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddToSchedulingListModal;
